import { useState } from 'react';
import { Plus, Check, X } from 'lucide-react';
import {
  MAX_PERKS,
  MAX_PERK_LENGTH,
  PRESET_PERKS,
  normalizePerk,
  type CreatorDraftPayload,
} from './draftSchema';

type Props = {
  payload: CreatorDraftPayload;
  onChange: (partial: Partial<CreatorDraftPayload>) => void;
};

const StepPerks = ({ payload, onChange }: Props) => {
  const [customDraft, setCustomDraft] = useState('');
  const perks = Array.isArray(payload.perks) ? payload.perks : [];

  const togglePreset = (preset: string) => {
    if (perks.includes(preset)) {
      onChange({ perks: perks.filter((p) => p !== preset) });
    } else if (perks.length < MAX_PERKS) {
      onChange({ perks: [...perks, preset] });
    }
  };

  const removePerk = (perk: string) => {
    onChange({ perks: perks.filter((p) => p !== perk) });
  };

  const addCustom = () => {
    const cleaned = normalizePerk(customDraft);
    if (!cleaned) return;
    if (perks.includes(cleaned)) {
      setCustomDraft('');
      return;
    }
    if (perks.length >= MAX_PERKS) return;
    onChange({ perks: [...perks, cleaned] });
    setCustomDraft('');
  };

  return (
    <div className="creator-step" data-testid="step-perks">
      <header className="creator-step__header">
        <span className="creator-step__index">4</span>
        <div>
          <h2 className="creator-step__title">Perks</h2>
          <p className="creator-step__subtitle">
            Choose the benefits that make this membership worth joining and easy to explain.
          </p>
        </div>
      </header>

      <div className="creator-field">
        <span className="creator-field__label">
          SELECTED PERKS
          <span className="creator-field__counter">
            {perks.length} / {MAX_PERKS}
          </span>
        </span>
        {perks.length === 0 ? (
          <div className="creator-perks__empty" data-testid="creator-perks-empty">
            Pick at least one perk from the presets below or add a custom one.
          </div>
        ) : (
          <div className="creator-perks__selected" data-testid="creator-perks-selected">
            {perks.map((perk) => (
              <button
                key={perk}
                type="button"
                className="creator-perks__chip creator-perks__chip--selected"
                onClick={() => removePerk(perk)}
                data-testid={`creator-perks-remove-${perk}`}
                aria-label={`Remove perk: ${perk}`}
              >
                <Check size={12} />
                <span>{perk}</span>
                <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="creator-field">
        <span className="creator-field__label">QUICK ADD FROM PRESETS</span>
        <div className="creator-perks__presets">
          {PRESET_PERKS.map((preset) => {
            const isSelected = perks.includes(preset);
            const atCap = !isSelected && perks.length >= MAX_PERKS;
            return (
              <button
                key={preset}
                type="button"
                className={`creator-perks__chip${isSelected ? ' creator-perks__chip--muted' : ''}`}
                onClick={() => togglePreset(preset)}
                disabled={atCap}
                data-testid={`creator-perks-preset-${preset.replace(/\s+/g, '-').replace(/\//g, '').toLowerCase()}`}
              >
                {isSelected ? <Check size={12} /> : <Plus size={12} />}
                <span>{preset}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="creator-field">
        <span className="creator-field__label">
          ADD A CUSTOM PERK
          <span className="creator-field__counter creator-field__counter--muted">
            {customDraft.length} / {MAX_PERK_LENGTH}
          </span>
        </span>
        <div className="creator-perks__custom-row">
          <input
            type="text"
            className="creator-field__input"
            placeholder="e.g. Free home delivery on orders over ₹500"
            value={customDraft}
            maxLength={MAX_PERK_LENGTH}
            onChange={(event) => setCustomDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addCustom();
              }
            }}
            data-testid="creator-perks-custom-input"
          />
          <button
            type="button"
            className="creator-perks__custom-add"
            onClick={addCustom}
            disabled={
              !normalizePerk(customDraft)
              || perks.length >= MAX_PERKS
              || perks.includes(normalizePerk(customDraft))
            }
            data-testid="creator-perks-custom-add"
          >
            <Plus size={14} />
            <span>Add</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepPerks;
